/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { errorMessage } from '@/util/errorUtil'
import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { logWarn } from '@amazon/vinyl-util'
import type { I18nString } from '@amzn/amp-i18n'
import { ctx } from '@/util/context'
import { globalData } from '@amzn/amp-playback'

const { target } = ctx('mutationUndoStack')

/** Default lifetime of a mutation undo entry in ms. */
export const MUTATION_UNDO_TTL_MS = 5000

/**
 * A single "you can still undo this mutation" entry. Distinct from
 * `blackbolt/dismissStack` (which owns feed-card dismissals + stores
 * enough state to re-insert the card) — this one only knows the copy
 * to show and the callback to invoke on Undo.
 *
 * `count` grows when successive `pushUndoable` calls with the same
 * `message` land while the entry is still pending, and the individual
 * `undo` callbacks are chained so a single Undo click rolls back the
 * whole batch. `mergedFromRepeat` distinguishes an entry whose count
 * grew via rapid repeat-clicks (display should show `(×N)`) from one
 * whose initial input `count` was already `> 1` (caller baked the
 * multiplicity into `message`, don't re-decorate). See `UndoToast.tsx`
 * for the display format.
 */
export interface MutationUndoEntry {
    readonly id: number
    readonly message: I18nString
    /** Total number of merged mutations, >= 1. */
    readonly count: number
    /**
     * True when the count grew via batching (successive
     * `pushUndoable` calls with the same message). False when the
     * caller passed `count > 1` on the first push — that path
     * assumes `message` already reads pluralized.
     */
    readonly mergedFromRepeat: boolean
    readonly undo: () => void
    readonly expiresAt: number
}

const stack = globalData(() => data<readonly MutationUndoEntry[]>([]))
/**
 * Public read-only view. Newest entry is at the TAIL so `UndoToast`
 * reads the most-recent mutation from `at(-1)` mirroring the existing
 * dismiss-stack convention.
 */
export const mutationUndoStack: ObservableValue<readonly MutationUndoEntry[]> = stack

export interface PushUndoableInput {
    /**
     * User-facing copy for the toast. Two rapid calls with the SAME
     * `message` merge — the merged entry's count grows and both undo
     * callbacks fire on a single Undo click.
     */
    readonly message: I18nString
    /** Number of mutations this call represents. Defaults to 1. */
    readonly count?: number
    /**
     * Invoked when the user clicks "Undo". Not called on timeout.
     * For merged (batched) entries the individual `undo` callbacks
     * are chained in push order.
     */
    readonly undo: () => void
    /**
     * Auto-dismiss delay. Defaults to `MUTATION_UNDO_TTL_MS` (5s).
     */
    readonly ttlMs?: number
}

let nextId = 1
const scheduled = new Map<number, ReturnType<typeof setTimeout>>()

/**
 * Push a mutation onto the undo stack. Shows a batch-style toast
 * ("Added 5 tracks to Chill Playlist — Undo") for `ttlMs` ms. Clicking
 * Undo pops the entry and invokes the merged `undo` callbacks. Timeout
 * evicts the entry silently.
 *
 * Batching: if the current tail entry has the same `message`, this
 * call is folded into it — `count` is added and the new `undo` is
 * chained onto the existing one. The tail's TTL is refreshed so the
 * merged toast lives for another `ttlMs` window.
 */
export function pushUndoable(input: PushUndoableInput): MutationUndoEntry {
    const inCount = input.count ?? 1
    const ttl = input.ttlMs ?? MUTATION_UNDO_TTL_MS
    const current = stack.value
    const tail = current.at(-1)
    // Only batch when both the pending tail AND this push carry an
    // implicit count of 1 — a caller who passes count > 1 on either
    // side has baked the multiplicity into `message` and we don't
    // decorate that.
    const canBatch =
        tail != null && tail.message === input.message && inCount === 1 && tail.count >= 1
    if (canBatch) {
        // Merge into the tail. Chain undos in push order so a rollback
        // walks the batch newest-last (mirrors the way each individual
        // caller flipped its own state — the last write wins on undo).
        const priorUndo = tail.undo
        const merged: MutationUndoEntry = {
            id: tail.id,
            message: tail.message,
            count: tail.count + inCount,
            mergedFromRepeat: true,
            undo: () => {
                priorUndo()
                input.undo()
            },
            expiresAt: Date.now() + ttl,
        }
        stack.value = current.slice(0, -1).concat([merged])
        // Reset the tail's timer so the merged toast lives another ttl.
        clearTimeout(scheduled.get(tail.id))
        scheduleEvict(merged, ttl)
        return merged
    }
    const entry: MutationUndoEntry = {
        id: nextId++,
        message: input.message,
        count: inCount,
        mergedFromRepeat: false,
        undo: input.undo,
        expiresAt: Date.now() + ttl,
    }
    stack.value = current.concat([entry])
    scheduleEvict(entry, ttl)
    return entry
}

/**
 * Pop the tail entry and invoke its (possibly chained) undo callback.
 * No-op when the stack is empty. Cancels any pending eviction timer.
 */
export function undoLastMutation(): MutationUndoEntry | null {
    const current = stack.value
    if (current.length === 0) return null
    const entry = current[current.length - 1]
    stack.value = current.slice(0, -1)
    clearTimeout(scheduled.get(entry.id))
    scheduled.delete(entry.id)
    try {
        entry.undo()
    } catch (err) {
        // Surface the failure but keep the pop — a throwing undo
        // must not wedge the stack against every subsequent mutation.
        logWarn(target, 'undoLastMutation callback threw', {
            error: errorMessage(err),
        })
    }
    return entry
}

/**
 * Dismiss the tail entry without invoking its undo callback. Used by
 * the auto-timeout path — the toast fades and the mutation persists.
 */
function evictEntry(entry: MutationUndoEntry): void {
    scheduled.delete(entry.id)
    stack.value = stack.value.filter((e) => e.id !== entry.id)
}

function scheduleEvict(entry: MutationUndoEntry, ttl: number): void {
    const timer = setTimeout(() => evictEntry(entry), ttl)
    scheduled.set(entry.id, timer)
}

/**
 * Test / HMR reset. Clears the stack + cancels every pending timer.
 */
export function resetMutationUndoStackForTests(): void {
    for (const timer of scheduled.values()) clearTimeout(timer)
    scheduled.clear()
    stack.value = []
}
